import { type FC, memo } from "react";
import { Link, LinkKind, Text } from "@stubhub/broadway-web";
import type { EventListGridItem } from "src/http/api-types";
import { FormatContent, useContent, useFormattedContent } from "src/shared/components/content";
import { SeoTable, type SeoTableColumn } from "src/website-apps/shared/components/seo-table/SeoTable";

interface Props {
  rows: readonly EventListGridItem[];
  performerName: string;
}

export const SportsScheduleTable: FC<Props> = memo(({ rows, performerName }) => {
  /** "Event" */
  const eventHeader = useContent("cid$#4374580#");
  /** "Opponent" */
  const opponentHeader = useContent("cid$#3678622#");
  /** "Location" */
  const locationHeader = useContent("cid$#4374392#");
  /** "Venue" */
  const venueHeader = useContent("cid$#4374393#");
  /** "TBA" */
  const tbaLabel = useContent("cid$#3656911#");
  // "{0} Schedule"
  const headingText = useFormattedContent("cid$#4379479#", [performerName]);

  const columns: SeoTableColumn<EventListGridItem>[] = [
    {
      header: eventHeader,
      renderCell: (event) => {
        const opponent = event.opponentName ?? "";
        const dateText = event.isDateConfirmed ? (event.formattedDate ?? tbaLabel) : tbaLabel;
        const matchup =
          event.isHomeGame === null || event.isHomeGame === undefined ? (
            opponent
          ) : event.isHomeGame ? (
            <FormatContent id="cid$#4379484#" params={[opponent]} /> /* "vs {0}" */
          ) : (
            <FormatContent id="cid$#4379483#" params={[opponent]} />
          ); /* "@ {0}" */
        return (
          <Link href={event.url ?? "#"} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {matchup} - {dateText}
            </Text>
          </Link>
        );
      },
    },
    {
      header: opponentHeader,
      renderCell: (event) =>
        event.opponentUrl ? (
          <Link href={event.opponentUrl} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {event.opponentName ?? ""}
            </Text>
          </Link>
        ) : (
          <Text variant="body" size="small" as="span">
            {event.opponentName ?? ""}
          </Text>
        ),
    },
    {
      header: locationHeader,
      renderCell: (event) =>
        event.cityUrl ? (
          <Link href={event.cityUrl} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {event.formattedVenueLocation ?? ""}
            </Text>
          </Link>
        ) : (
          <Text variant="body" size="small" as="span">
            {event.formattedVenueLocation ?? ""}
          </Text>
        ),
    },
    {
      header: venueHeader,
      renderCell: (event) =>
        event.venueUrl ? (
          <Link href={event.venueUrl} kind={LinkKind.Ghost}>
            <Text variant="body" size="small" as="span">
              {event.venueName ?? ""}
            </Text>
          </Link>
        ) : (
          <Text variant="body" size="small" as="span">
            {event.venueName ?? ""}
          </Text>
        ),
    },
  ];

  return (
    <SeoTable
      rows={rows}
      heading={headingText}
      caption={headingText}
      columns={columns}
      rowKey={(event) => event.eventId}
    />
  );
});
SportsScheduleTable.displayName = "SportsScheduleTable";
